import React, { useState, useEffect } from "react";
import {
    Box, Typography, Button, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow,
    Chip, IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import UserDialogBox from "./DialogBox/UserDialogBox";
import useAdminControlsStore from "../../Stores/AdminControlsStore";
import { User } from "../../Types/User"
import API from "../../api2";

function AdminBase() {
    const [users, setUsers] = useState<User[]>([]);

    useEffect(() => {
        const fetchUsers = async () => {
            const response = await API.getAllUsers();
            setUsers(response.data);
        };
        fetchUsers();
    }, []);

    // Dialog and form state
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const { replaceUser } = useAdminControlsStore();

    // Handle opening dialog for creating new user
    const handleCreateUser = () => {
        setDialogMode('create');
        setOpenDialog(true);
    };

    // Handle opening dialog for editing existing user
    const handleEditUser = (user: User) => {
        replaceUser({
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            accountLevel: user.accountLevel,
            active: user.active,
            resetPassword: false,
            emailVerified: user.emailVerified,
            customers: user.customers,
            mode: user.mode,
        });
        setDialogMode('edit');
        setOpenDialog(true);
    };

    // Handle closing the dialog
    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    User Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateUser}
                >
                    Create User
                </Button>
            </Box>

            <Paper elevation={2} sx={{ overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 600 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Account Level</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow
                                    key={user.id}
                                    hover
                                    sx={!user.active ? { opacity: 0.7 } : undefined}
                                >
                                    <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>{user.accountLevel}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={user.active ? "Active" : "Inactive"}
                                            color={user.active ? "success" : "default"}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleEditUser(user)}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
            <UserDialogBox
                openDialog={openDialog}
                dialogMode={dialogMode}
                handleCloseDialog={handleCloseDialog}
            />
        </Box>
    );
}

export default AdminBase;